// src/ui/context/AuthContext.tsx
import { h, createContext } from "preact";
import { useState, useContext, useEffect } from "preact/hooks";
import type { AuthStatus, AuthError } from "../../types";

interface AuthContextType {
    authStatus: AuthStatus;
    login: (email: string, password: string) => Promise<void>;
    signup: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    checkSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({
    children,
}: {
    children: preact.ComponentChildren;
}) {
    const [authStatus, setAuthStatus] = useState<AuthStatus>("loading");

    const checkSession = async () => {
        try {
            const response = await fetch("/auth/session", {
                credentials: "include",
            });

            // Even if we get a 401, that's not an error - it's an expected state
            if (response.status === 401) {
                setAuthStatus("unauthenticated");
                return;
            }

            if (!response.ok) {
                // Only treat non-401 errors as actual errors
                console.error("Session check failed:", response.status);
                setAuthStatus("unauthenticated");
                return;
            }

            setAuthStatus("authenticated");
        } catch (error) {
            console.error("Session check failed:", error);
            setAuthStatus("unauthenticated");
        }
    };

    const signup = async (email: string, password: string) => {
        const response = await fetch("/auth/signup", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
            const error = (await response.json()) as AuthError;
            throw new Error(error.error || "Signup failed");
        }

        // After successful signup, we're automatically logged in
        setAuthStatus("authenticated");
    };

    const login = async (email: string, password: string) => {
        const response = await fetch("/auth/login", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
            const error = (await response.json()) as AuthError;
            throw new Error(error.error || "Login failed");
        }

        setAuthStatus("authenticated");
    };

    const logout = async () => {
        try {
            const response = await fetch("/auth/logout", {
                method: "POST",
                credentials: "include",
            });

            if (!response.ok) {
                throw new Error("Logout failed");
            }

            setAuthStatus("unauthenticated");
        } catch (error) {
            console.error("Logout failed:", error);
            throw error;
        }
    };

    useEffect(() => {
        checkSession();
    }, []);

    return (
        <AuthContext.Provider
            value={{
                authStatus,
                login,
                signup,
                logout,
                checkSession,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
